import { Link, Navigate } from 'react-router-dom';
import {
  type LucideIcon,
  ClipboardList,
  Users,
  ListChecks,
  ScanLine,
  Activity,
  AlertTriangle,
  ShieldAlert,
  Settings,
  Printer,
  FileText,
  Stethoscope,
  HardHat,
  Wrench,
  Truck,
  FileSignature,
  Mail,
  Award,
  Boxes,
  ClipboardCheck,
  CalendarClock,
  CalendarDays,
  Database,
  Plug,
  History,
  UserCog,
  User as UserIcon,
} from 'lucide-react';
import { VISIBLE_STATIONS, stationLabel, hasStaffRole, type Station } from '@hcc/types';
import { useCurrentUser, type SessionUser } from '../hooks/useAuth';
import { useCurrentScope } from '../hooks/useMobilisations';

/**
 * Landing dashboard. Shows role-gated tiles so users see only the
 * features they can actually use. Admin sees everything; station staff
 * see their station; inductees see self-service.
 *
 * Visual brief (from the user): "really good first impression". The
 * tile grid is the surface the most people see most often, so it gets
 * real iconography (lucide-react), generous spacing, and a subtle
 * hover lift to telegraph that tiles are interactive.
 */
export function Home() {
  const { data: user, isLoading } = useCurrentUser();
  const scope = useCurrentScope();

  if (isLoading) {
    return (
      <main className="min-h-screen bg-bg p-6 text-fg">
        <p className="text-sm text-fg-muted">Loading...</p>
      </main>
    );
  }

  // No "Sign in" intermediate page — bounce straight to /login, which
  // shows the actual Microsoft SSO button (and dev / emergency fallbacks
  // when configured). Saves the user a redundant click.
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // A pure inductee (no staff role, linked to an attendee record) only has
  // their own onboarding/induction. Skip the single-tile landing and send
  // them straight to /me — fewer clicks, less confusion.
  if (!isStaff(user) && user.person_id) {
    return <Navigate to="/me" replace />;
  }

  const groups = visibleGroups(user, scope?.kind === 'onboarding');
  const noTiles = groups.every((g) => g.tiles.length === 0);

  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <section className="animate-fade-in">
          <h1 className="text-3xl font-semibold tracking-tight">
            Welcome, {user.display_name.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">Signed in as {user.email}.</p>
          <p className="mt-2 text-xs text-fg-muted">
            Every section below is also in the sidebar.
          </p>
        </section>

        {noTiles ? (
          <section className="mt-6 flex items-start gap-3 rounded-lg border border-warning/25 bg-warning-soft/15 p-4 text-sm text-warning-soft-fg">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <strong>No permissions yet.</strong> Your account exists but no
              roles have been assigned. Ask an admin to grant access.
            </div>
          </section>
        ) : null}

        {groups.map((g) =>
          g.tiles.length === 0 ? null : (
            <section
              key={g.title}
              id={g.title.toLowerCase().replace(/\s+/g, '-')}
              className="mt-10 scroll-mt-20"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                {g.title}
              </h2>
              <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {g.tiles.map((t) => (
                  <Tile key={t.to} tile={t} />
                ))}
              </div>
            </section>
          ),
        )}
      </div>
    </main>
  );
}

function Tile({ tile }: { tile: Tile }) {
  const Icon = tile.icon;
  return (
    <Link
      to={tile.to}
      className="group relative overflow-hidden rounded-xl border border-line/10 bg-surface p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong/20 hover:shadow-md"
    >
      <div className="flex items-start gap-4">
        <div
          aria-hidden
          className={`flex h-11 w-11 flex-none items-center justify-center rounded-lg ${tile.iconBg}`}
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0 pt-0.5">
          <div className="text-[15px] font-semibold tracking-tight text-fg group-hover:text-fg-muted">
            {tile.title}
          </div>
          <div className="mt-1 text-xs leading-relaxed text-fg-muted">
            {tile.description}
          </div>
        </div>
      </div>
    </Link>
  );
}

interface Tile {
  to: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconBg: string;
}

interface Group {
  title: string;
  tiles: Tile[];
}

const ADMIN = ['admin', 'super_admin'];
const COORD = ['admin', 'super_admin', 'coordinator'];
const HR_LIKE = ['admin', 'super_admin', 'coordinator', 'hr'];

function hasAny(user: SessionUser, allowed: readonly string[]): boolean {
  return user.roles.some((r) => allowed.includes(r));
}

/** Staff = anything beyond the self-service roles (inductee, learner). */
function isStaff(user: SessionUser): boolean {
  return hasStaffRole(user.roles);
}

const STATION_ICONS: Record<Station, LucideIcon> = {
  IT: ScanLine,
  PPE: HardHat,
  Tools: Wrench,
  Fleet: Truck,
  DBSHR: Stethoscope,
};

function visibleGroups(user: SessionUser, dayToDay: boolean): Group[] {
  const onTheDay: Tile[] = [];
  const reports: Tile[] = [];
  const admin: Tile[] = [];
  const me: Tile[] = [];

  // Reception, stations and the coordinator view are mobilisation-day
  // surfaces; day-to-day onboarding hides them (kit is issued directly).
  const isReception = user.roles.includes('reception') || hasAny(user, COORD);
  if (!dayToDay && isReception) {
    onTheDay.push({
      to: '/reception',
      title: 'Reception check-in',
      description: 'Search arrivals, mark people on site.',
      icon: ClipboardList,
      iconBg: 'bg-success-soft/15 text-success-soft-fg',
    });
  }

  if (!dayToDay) {
    for (const s of VISIBLE_STATIONS) {
      const role = `station_${s.toLowerCase()}`;
      if (user.roles.includes(role) || hasAny(user, COORD)) {
        onTheDay.push({
          to: `/stations/${s}`,
          title: `Station: ${stationLabel(s)}`,
          description: `Issue ${stationLabel(s)} kit and capture signatures.`,
          icon: STATION_ICONS[s],
          iconBg: 'bg-info-soft/15 text-info-soft-fg',
        });
      }
    }
  }

  if (!dayToDay && hasAny(user, COORD)) {
    onTheDay.push({
      to: '/coordinator',
      title: 'Coordinator view',
      description: 'Global per-station status across all attendees.',
      icon: Activity,
      iconBg: 'bg-accent/10 text-accent',
    });
  }

  // Documents and Register are staff-only; inductees see their own
  // record via the "My record" tile lower down. The register is the
  // day-of roll-call, so it drops out day-to-day; documents stay.
  if (isStaff(user)) {
    onTheDay.push({
      to: '/documents',
      title: 'Documents',
      description: 'Attach driving licences, certs, anything you photograph on the day.',
      icon: FileSignature,
      iconBg: 'bg-teal-100 text-accent',
    });
    if (!dayToDay) {
      onTheDay.push({
        to: '/register',
        title: 'Register',
        description: 'Live list of everyone on site. Print in two clicks.',
        icon: ClipboardCheck,
        iconBg: 'bg-danger-soft/15 text-danger-soft-fg',
      });
    }
  }

  if (hasAny(user, HR_LIKE)) {
    reports.push({
      to: '/reports',
      title: 'Reports',
      description: 'Attendance, no-shows, outstanding kit, asset register.',
      icon: ListChecks,
      iconBg: 'bg-warning-soft/15 text-warning-soft-fg',
    });
  }

  // Equipment is visible to coord+ AND any station role (station staff
  // manage their own station's pool). Push before the coord/admin tiles
  // so it sits naturally near kit templates.
  const hasStationRoleTile = VISIBLE_STATIONS.some((s) =>
    user.roles.includes(`station_${s.toLowerCase()}`),
  );
  if (hasAny(user, COORD) || hasStationRoleTile) {
    admin.push({
      to: '/admin/assets',
      title: 'Equipment',
      description: 'Master register of physical assets, with pre-assignment.',
      icon: Boxes,
      iconBg: 'bg-accent/10 text-accent',
    });
  }

  if (hasAny(user, ADMIN)) {
    admin.push(
      {
        to: '/admin/users',
        title: 'Users & roles',
        description: 'Assign roles, link users to attendee records.',
        icon: UserCog,
        iconBg: 'bg-accent/10 text-accent',
      },
      {
        to: '/admin/emergency-access',
        title: 'Emergency-access log',
        description: 'Audit trail of every action under emergency mode.',
        icon: ShieldAlert,
        iconBg: 'bg-danger-soft/15 text-danger-soft-fg',
      },
      {
        to: '/admin/access-log',
        title: 'Access log',
        description: 'Audit trail of sensitive reads and writes by staff.',
        icon: History,
        iconBg: 'bg-danger-soft/15 text-danger-soft-fg',
      },
      {
        to: '/admin/system',
        title: 'System status',
        description: 'Read-only view of feature flags and configuration.',
        icon: Settings,
        iconBg: 'bg-surface-inset text-fg-muted',
      },
    );
  }

  if (!dayToDay && hasAny(user, HR_LIKE)) {
    admin.push({
      to: '/admin/printable',
      title: 'Printable fallback',
      description: 'Paper attendee + kit sheet for the day-of safety net.',
      icon: Printer,
      iconBg: 'bg-accent/10 text-accent',
    });
  }

  if (hasAny(user, COORD)) {
    admin.push(
      {
        to: '/admin/mobilisations',
        title: 'Mobilisations',
        description: 'Create, edit and switch between mobilisation events.',
        icon: CalendarDays,
        iconBg: 'bg-accent/10 text-accent',
      },
      {
        to: '/admin/import-attendees',
        title: 'Import attendees',
        description: 'Bulk-import a CSV of people for an upcoming mobilisation.',
        icon: Users,
        iconBg: 'bg-accent/10 text-accent',
      },
      {
        to: '/admin/kit-templates',
        title: 'Kit templates',
        description: 'Per-position lists of expected kit across stations.',
        icon: FileText,
        iconBg: 'bg-accent/10 text-accent',
      },
      {
        to: '/admin/pre-day-issue',
        // "Early" is only meaningful against a mobilisation day.
        title: dayToDay ? 'Issue kit' : 'Issue kit early',
        description: dayToDay
          ? 'Issue kit to a new starter with full signature and PDF receipt.'
          : 'Hand out kit before mobilisation day with full signature and PDF receipt.',
        icon: CalendarClock,
        iconBg: 'bg-warning-soft/15 text-warning-soft-fg',
      },
      // Annual PPE audit tile intentionally hidden — feature paused
      // until the audit cycle starts. Route still exists.
      // Completion certificates and the feedback survey are bulk post-day
      // sends for a mobilisation, so they drop out of day-to-day onboarding.
      ...(!dayToDay
        ? [
            {
              to: '/admin/certificates',
              title: 'Completion certificates',
              description: 'Generate and email completion PDFs after the day.',
              icon: Award,
              iconBg: 'bg-accent/10 text-accent',
            },
            {
              to: '/admin/feedback',
              title: 'Feedback survey',
              description: 'Send the post-day survey link to attendees.',
              icon: Mail,
              iconBg: 'bg-accent/10 text-accent',
            },
          ]
        : []),
      {
        to: '/admin/planon',
        title: 'Planon integration',
        description: 'Status and backfill of the asset-register webhook.',
        icon: Plug,
        iconBg: 'bg-accent/10 text-accent',
      },
    );
  }
  if (hasAny(user, HR_LIKE)) {
    admin.push(
      {
        to: '/admin/people',
        title: 'People',
        description: 'Notes, pre-employment checklist and event history per attendee.',
        icon: Database,
        iconBg: 'bg-accent/10 text-accent',
      },
      // Leaver returns tile intentionally hidden — feature paused
      // until the use case lands. Route still exists.
    );
  }
  if (user.person_id) {
    me.push({
      to: '/me',
      title: 'My record',
      description: 'View your own kit, signatures and outstanding items.',
      icon: UserIcon,
      iconBg: 'bg-surface-inset text-fg-muted',
    });
  }

  return [
    { title: dayToDay ? 'Day to day' : 'On the day', tiles: onTheDay },
    { title: 'Reports', tiles: reports },
    { title: 'Admin', tiles: admin },
    { title: 'You', tiles: me },
  ];
}
